package com.bikompanion.data

import kotlinx.serialization.json.Json
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.ParkingType

internal val overpassJson = Json { ignoreUnknownKeys = true }

/**
 * The Overpass instances to try, best first.
 *
 * More than one because a single host is a single point of failure the map cannot survive. The
 * main instance refuses TCP outright when it is overloaded or has blacklisted the caller's
 * address — a connection failure, not a 429 — and with one endpoint configured that reads to
 * every rider at once as "there are no racks anywhere". It was doing exactly that, from two
 * different networks, while this list was one entry long.
 *
 * The main instance stays first: it is the reference implementation, the best synced, and the
 * one whose terms the app is written against. `OverpassEndpointRacer` starts the next one
 * alongside it after a few seconds rather than waiting it out, and `EndpointHealth` drops one
 * that has just refused us, so leading with it costs nothing when it is having a bad day.
 *
 * `overpass.osm.ch` is deliberately absent. It answers 200 with well-formed JSON, an empty
 * `elements`, and an `osm3s.timestamp_osm_base` of "116776" where every healthy instance puts a
 * date — so nothing downstream can tell its silence from an area that genuinely has no racks,
 * and that is the one failure this layer must never hand on as an answer.
 *
 * `maps.mail.ru` is left out for privacy: every endpoint here receives the map areas riders look
 * at, and the privacy policy only names operators it can vouch for.
 */
internal val OverpassEndpoints = listOf(
    "https://overpass-api.de/api/interpreter",
    "https://overpass.private.coffee/api/interpreter",
    "https://overpass.kumi.systems/api/interpreter",
)

/**
 * Asks for every bike parking in a box, whatever shape it was mapped as.
 *
 * `nwr` rather than `node`: a bike garage or a large rack area is drawn as a way in OSM, so asking
 * only for nodes left those out entirely. Worldwide that is 140,135 ways and 572 relations against
 * 790,699 nodes — 15% of every bike parking mapped, and disproportionately the big covered ones.
 * `out center` gives them a single point to draw, which is all this layer shows.
 *
 * `[timeout:25]` is the budget Overpass itself works to. Telling it three minutes only meant it
 * kept working on requests the client had long since abandoned; a short budget makes it answer
 * with an error quickly, which is something the racer can act on.
 */
internal fun overpassQuery(
    south: Double,
    west: Double,
    north: Double,
    east: Double,
) = """[out:json][timeout:25];nwr["amenity"="bicycle_parking"]($south,$west,$north,$east);out center qt;"""

/**
 * Reads an Overpass answer into racks, dropping anything that isn't one.
 *
 * Deliberately not lenient about the document itself: Overpass reports a rate limit or an
 * overload as an HTML error page with a 200, so a body that doesn't parse has to throw. Made
 * to return an empty list instead, a throttled request would read as "no racks here" and get
 * cached as the truth.
 */
internal fun parseBikeParking(overpassJsonStr: String): List<BikeParkingSpot> =
    overpassJson.decodeFromString<OverpassResult>(overpassJsonStr).elements.mapNotNull { element ->
        BikeParkingSpot(
            // Typed, because ids are only unique within a type: node 42 and way 42 are two
            // different parkings, and collapsing them would drop one of the pair.
            id = "${element.type ?: "node"}/${element.id}",
            // A way or relation has no position of its own, only the centre `out center` adds.
            // Anything with neither can't be drawn; null island is not where it is.
            latitude = element.lat ?: element.center?.lat ?: return@mapNotNull null,
            longitude = element.lon ?: element.center?.lon ?: return@mapNotNull null,
            // A capacity that isn't a plain number ("yes", "10+", "2 stands") tells us nothing
            // we can put in front of a rider, so it reads the same as untagged.
            capacity = element.tags?.capacity?.toIntOrNull(),
            isCovered = element.tags?.covered?.toBooleanTag(),
            address = element.tags?.toAddress(),
            type = element.tags?.bicycleParking.toParkingType(),
        )
    }

/** Joins the available address tags into a single line. */
private fun OverpassTags.toAddress(): String? {
    val parts = listOfNotNull(street, houseNumber, city).filter { it.isNotBlank() }
    return parts.joinToString(" ").takeIf { it.isNotEmpty() }
}

/** OSM's yes/no, which is not Kotlin's — and is absent far more often than it is either. */
private fun String.toBooleanTag(): Boolean? =
    when (this) {
        "yes" -> true
        "no" -> false
        else -> null
    }

/**
 * OSM's `bicycle_parking` values collapsed onto [ParkingType]'s small set of icons. Anything
 * untagged, or a value not covered here, reads as [ParkingType.Other].
 */
internal fun String?.toParkingType(): ParkingType =
    when (this) {
        "stands", "wall_loops", "rack", "bollard", "wide_stands", "handlebar_holder",
        "streetpole", "crossbar", "anchors", "two-tier", "safe_loops", "ground_slots",
        -> ParkingType.Stands

        "shed", "tree" -> ParkingType.Shed

        "lockers" -> ParkingType.Lockers

        "building", "garage" -> ParkingType.Building

        else -> ParkingType.Other
    }
